<script setup>
import { computed } from 'vue';
import { useForm } from '@inertiajs/vue3';

const props = defineProps({
    comparison: {
        type: Object,
        default: () => ({
            primary_program_id: null,
            primary_program: null,
            secondary_program_id: null,
            secondary_program: null,
            is_active: true,
        })
    }
});

const comparisonForm = useForm({
    ...props.comparison
});

const getTermMonths = (term) => {
    const months = Number(term?.term_month?.months);
    return Number.isNaN(months) ? null : months;
};

// Map: term months -> term
// 68 months -> term
// 84 months -> term
const buildTermMap = (terms = []) => {
    return (terms || [])
        .filter((term) => getTermMonths(term) !== null)
        .sort((a, b) => {
            const byMonths = (getTermMonths(a) ?? 0) - (getTermMonths(b) ?? 0);
            if (byMonths !== 0) {
                return byMonths;
            }
            return (a.order ?? 0) - (b.order ?? 0);
        })
        .reduce((acc, term) => {
            const months = getTermMonths(term);
            if (months !== null && !acc.has(months)) {
                acc.set(months, term);
            }
            return acc;
        }, new Map());
};

const matchedTermPairs = computed(() => {
    const primaryTermsMap = buildTermMap(comparisonForm.primary_program?.terms ?? []);
    const secondaryTermsMap = buildTermMap(comparisonForm.secondary_program?.terms ?? []);
    return [...primaryTermsMap.keys()]
        .filter((months) => secondaryTermsMap.has(months))
        .sort((a, b) => a - b)
        .map((months) => ({
            months,
            primaryTerm: primaryTermsMap.get(months),
            secondaryTerm: secondaryTermsMap.get(months),
        }));
});

/**
 * Build band-tier pairs for comparison: only tiers with the SAME comparison_band_tier_id are compared.
 * If a program doesn't have a tier for a band, that side defaults to 0.
 * Bands are collected from both primary and secondary terms.
 */
function buildMatchedBandTierPairs(primaryTerm, secondaryTerm) {
    const bandIdToTier = (term) => {
        const map = new Map();
        for (const tier of term?.tiers ?? []) {
            const bandId = tier.comparison_band_tier_id ?? tier.comparisonBandTier?.id ?? tier.comparison_band_tier?.id;
            if (bandId != null && !map.has(bandId)) {
                map.set(bandId, tier);
            }
        }
        return map;
    };
    const primaryMap = bandIdToTier(primaryTerm);
    const secondaryMap = bandIdToTier(secondaryTerm);
    const allBandIds = [...new Set([...primaryMap.keys(), ...secondaryMap.keys()])];

    return allBandIds
        .map((bandId) => {
            const primaryTier = primaryMap.get(bandId) ?? null;
            const secondaryTier = secondaryMap.get(bandId) ?? null;
            const cbt = primaryTier?.comparison_band_tier ?? primaryTier?.comparisonBandTier
                ?? secondaryTier?.comparison_band_tier ?? secondaryTier?.comparisonBandTier;
            const comparativeValue = Number(cbt?.comparative_value ?? 0);
            const primaryCount = primaryTier?.rates?.length ?? 0;
            const secondaryCount = secondaryTier?.rates?.length ?? 0;
            const maxRateCount = Math.max(primaryCount, secondaryCount, 1);
            return {
                bandId,
                bandLabel: cbt?.label ?? '',
                comparativeValue,
                primaryTier,
                secondaryTier,
                maxRateCount,
            };
        })
        .sort((a, b) => a.comparativeValue - b.comparativeValue);
}

function getPayoutAmountValue(tier, rateIndex) {
    if (!tier || !tier.rates?.[rateIndex]) {
        return 0;
    }
    const rate = tier.rates[rateIndex];
    const cbt = tier.comparison_band_tier;
    const comparisonValue = Number(cbt?.comparative_value ?? 0);
    const reservePercent = Number(rate.reserve_percent ?? 0);
    const bonusAmount = Number(rate.bonus_amount ?? 0);
    return comparisonValue * reservePercent / 100 + bonusAmount;
}

function formatCurrency(value) {
    return Number(value ?? 0).toFixed(2);
}

function getPayoutDifferenceValue(primaryTier, secondaryTier, rateIndex) {
    return getPayoutAmountValue(primaryTier, rateIndex) - getPayoutAmountValue(secondaryTier, rateIndex);
}

const primaryLenderColor = computed(() => {
    return comparisonForm.primary_program?.lender?.color || '#000000';
});

const secondaryLenderColor = computed(() => {
    return comparisonForm.secondary_program?.lender?.color || '#000000';
});
</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.program_configuration') }}</template>
    <div class="p-4">
        <div class="overflow-x-auto shadow-lg rounded-xl border border-grey-300 bg-white">
            <table class="min-w-full text-left rtl:text-right text-body">
                <thead class="bg-neutral-secondary-soft text-body border-b rounded-t-xl border-grey-300">
                    <tr>
                        <th scope="col" class="px-6 py-4 font-semibold text-heading text-grey-700">
                            {{ $t('label.lender') }}
                        </th>
                        <th scope="col" class="px-6 py-4 font-semibold text-heading text-grey-700">
                            {{ $t('label.program_name') }}
                        </th>
                        <th scope="col" class="px-6 py-4 font-semibold text-heading text-grey-700">
                            {{ $t('label.program_code') }}
                        </th>
                        <th scope="col" class="px-6 py-4 font-semibold text-heading text-grey-700">
                            {{ $t('label.vehicle_min_year') }}
                        </th>
                        <th scope="col" class="px-6 py-4 font-semibold text-heading text-grey-700">
                            {{ $t('label.vehicle_max_year') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="even:bg-neutral-secondary-soft odd:bg-white hover:bg-blue-50 transition-colors border-b border-grey-200">
                        <th scope="row" class="px-6 py-4 font-medium text-heading whitespace-nowrap rounded-tl-xl">
                            {{ comparisonForm.primary_program?.lender?.code }}
                        </th>
                        <td class="px-6 py-4">
                            {{ comparisonForm.primary_program?.program_name }}
                        </td>
                        <td class="px-6 py-4">
                            {{ comparisonForm.primary_program?.program_code }}
                        </td>
                        <td class="px-6 py-4">
                            {{ comparisonForm.primary_program?.vehicle_min_year }}
                        </td>
                        <td class="px-6 py-4 rounded-tr-xl">
                            {{ comparisonForm.primary_program?.vehicle_max_year }}
                        </td>
                    </tr>
                    <tr class="even:bg-neutral-secondary-soft odd:bg-white hover:bg-blue-50 transition-colors">
                        <th scope="row" class="px-6 py-4 font-medium text-heading whitespace-nowrap rounded-bl-xl">
                            {{ comparisonForm.secondary_program?.lender?.code }}
                        </th>
                        <td class="px-6 py-4">
                            {{ comparisonForm.secondary_program?.program_name }}
                        </td>
                        <td class="px-6 py-4">
                            {{ comparisonForm.secondary_program?.program_code }}
                        </td>
                        <td class="px-6 py-4">
                            {{ comparisonForm.secondary_program?.vehicle_min_year }}
                        </td>
                        <td class="px-6 py-4 rounded-br-xl">
                            {{ comparisonForm.secondary_program?.vehicle_max_year }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <template v-if="matchedTermPairs.length">
            <div
                v-for="termPair in matchedTermPairs"
                :key="`pair-${termPair.months}`"
                class="mt-12">
                <div class="mb-3 text-base font-semibold text-grey-700">
                    {{ termPair.months }} {{ $t('label.term_months') }} {{  $t('label.vs') }} {{ termPair.months }} {{ $t('label.term_months') }}
                </div>

                <div class="grid grid-cols-7 shadow-lg rounded-xl border border-grey-300 bg-white">
                    <div
                        v-for="bandPair in buildMatchedBandTierPairs(termPair.primaryTerm, termPair.secondaryTerm)"
                        :key="`pair-${termPair.months}-band-${bandPair.bandId}`"
                        class="contents">

                        <div class="col-span-7 font-semibold text-center border-t border-r border-grey-300 py-2">
                            {{ bandPair.bandLabel }}
                        </div>

                        <div class="col-span-2 text-center content-center font-semibold row-span-2 border-t border-r border-grey-300">
                            {{ $t('label.rate_comparison') }}
                        </div>

                        <div class="col-span-2 text-center font-semibold border-t border-r border-grey-300 py-2">
                            <span :style="{ color: primaryLenderColor }">
                                {{ comparisonForm.primary_program?.lender?.code }}
                            </span>
                        </div>

                        <div class="col-span-2 text-center font-semibold border-t border-r border-grey-300 py-2">
                            <span :style="{ color: secondaryLenderColor }">
                                {{ comparisonForm.secondary_program?.lender?.code }}
                            </span>
                        </div>

                        <div class="col-span-1 content-center row-span-2 text-center border-t border-r border-grey-300">
                            {{ $t('label.comparison') }}
                        </div>

                        <div class="col-span-1 text-center border-t border-r border-grey-300">
                            {{ $t('label.details') }}
                        </div>

                        <div class="col-span-1 text-center border-t border-r border-grey-300">
                            {{ $t('label.payout') }}
                        </div>

                        <div class="col-span-1 text-center border-t border-r border-grey-300">
                            {{ $t('label.details') }}
                        </div>

                        <div class="col-span-1 text-center border-t border-r border-grey-300">
                            {{ $t('label.payout') }}
                        </div>

                        <div
                            v-for="rateIndex in bandPair.maxRateCount"
                            :key="`pair-${termPair.months}-band-${bandPair.bandId}-rate-${rateIndex - 1}`"
                            class="contents">

                            <div class="col-span-1 font-semibold row-span-2 text-center content-center border-t border-r border-grey-300">
                                <span :style="{ color: primaryLenderColor }">
                                    {{ (bandPair.primaryTier?.rates?.[rateIndex - 1]?.rate ?? 0) + '%' }}
                                </span>
                            </div>

                            <div class="col-span-1 font-semibold row-span-2 text-center content-center border-t border-r border-grey-300">
                                <span :style="{ color: secondaryLenderColor }">
                                    {{ (bandPair.secondaryTier?.rates?.[rateIndex - 1]?.rate ?? 0) + '%' }}
                                </span>
                            </div>

                            <div class="col-span-1 text-center border-t font-semibold border-r border-grey-300">
                                <span :style="{ color: primaryLenderColor }">
                                    {{ (bandPair.primaryTier?.rates?.[rateIndex - 1]?.reserve_percent != null ? bandPair.primaryTier.rates[rateIndex - 1].reserve_percent + '%' : '-') }}
                                </span>
                            </div>

                            <div class="col-span-1 row-span-2 font-semibold text-center content-center border-t border-r border-grey-300">
                                <span :style="{ color: primaryLenderColor }">
                                    {{ '$' + formatCurrency(getPayoutAmountValue(bandPair.primaryTier, rateIndex - 1)) }}
                                </span>
                            </div>

                            <div class="col-span-1 text-center border-t font-semibold border-r border-grey-300 content-center">
                                <span :style="{ color: secondaryLenderColor }">
                                    {{ (bandPair.secondaryTier?.rates?.[rateIndex - 1]?.reserve_percent != null ? bandPair.secondaryTier.rates[rateIndex - 1].reserve_percent + '%' : '-') }}
                                </span>
                            </div>

                            <div class="col-span-1 row-span-2 font-semibold text-center content-center border-t border-r border-grey-300">
                                <span :style="{ color: secondaryLenderColor }">
                                    {{ '$' + formatCurrency(getPayoutAmountValue(bandPair.secondaryTier, rateIndex - 1)) }}
                                </span>
                            </div>

                            <div
                                class="col-span-1 font-semibold row-span-2 text-center content-center border-t border-r border-grey-300"
                                :class="{
                                    'bg-green-200': getPayoutDifferenceValue(bandPair.primaryTier, bandPair.secondaryTier, rateIndex - 1) > 0,
                                    'bg-red-200': getPayoutDifferenceValue(bandPair.primaryTier, bandPair.secondaryTier, rateIndex - 1) <= 0
                                }">
                                {{ '$' + formatCurrency(getPayoutDifferenceValue(bandPair.primaryTier, bandPair.secondaryTier, rateIndex - 1)) }}
                            </div>

                            <div class="col-span-1 text-center border-t font-semibold border-r border-grey-300">
                                <span :style="{ color: primaryLenderColor }">
                                    {{ '$' + formatCurrency(bandPair.primaryTier?.rates?.[rateIndex - 1]?.bonus_amount ?? 0) }}
                                </span>
                            </div>

                            <div class="col-span-1 text-center border-t font-semibold border-r border-grey-300">
                                <span :style="{ color: secondaryLenderColor }">
                                    {{ '$' + formatCurrency(bandPair.secondaryTier?.rates?.[rateIndex - 1]?.bonus_amount ?? 0) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <div v-else class="text-center text-gray-500 py-8">
            {{ $t('label.no_programs_comparisons_found') }}
        </div>
    </div>
</MainContentLayout>
</template>
